/**
 * Bloco do plano de suporte SafePlace.
 *
 * Renderizado antes da tabela de investimento, para que o cliente entenda o que está incluído antes
 * de ver o preço. Suprimido quando o plano é `nenhum` — nesse caso já existe a nota explicativa na
 * seção de investimento.
 */

import { NOME_COMERCIAL_PLANO, type PlanoSuporte, type Trilha } from '@emaster/dominio';

import { escaparHtml } from './escapar.ts';
import { CONTAINER, icone } from './tokens.ts';

interface BeneficioPlano {
  readonly iconeNome: string;
  readonly texto: string;
}

const BENEFICIOS: Readonly<Record<Exclude<PlanoSuporte, 'nenhum'>, readonly BeneficioPlano[]>> = {
  developer: [
    { iconeNome: 'life-buoy', texto: 'Suporte técnico via Web, em horário comercial' },
    { iconeNome: 'clock', texto: 'SLA de até 24h para orientações, até 12h com sistema afetado' },
    { iconeNome: 'shield', texto: 'SafeGuardian Básico — visibilidade de custos e segurança' },
    { iconeNome: 'target', texto: 'Orientação de arquitetura geral para o seu ambiente' },
  ],
  business: [
    { iconeNome: 'headset', texto: 'Suporte premium por telefone, WhatsApp e Web' },
    { iconeNome: 'clock', texto: 'SLA de menos de 4h para produção afetada' },
    { iconeNome: 'activity', texto: 'Monitoramento 24x7 com alerta proativo' },
    { iconeNome: 'users', texto: 'Contato direto com arquitetos certificados, sem triagem' },
    { iconeNome: 'shield-check', texto: 'SafeGuardian Business — CloudOps, FinOps e Governança' },
    { iconeNome: 'hand-coins', texto: 'Concierge de faturamento e análise mensal de custos' },
  ],
  enterprise: [
    { iconeNome: 'headset', texto: 'Suporte 24x7 com TAM dedicado (Technical Account Manager)' },
    { iconeNome: 'clock', texto: 'SLA de menos de 15 minutos para sistemas essenciais inativos' },
    { iconeNome: 'activity', texto: 'Monitoramento 24x7 com NOC/SOC proativo' },
    { iconeNome: 'phone', texto: 'Acesso direto via WhatsApp ao especialista, sem triagens' },
    { iconeNome: 'ticket', texto: 'Chamados ilimitados — sem franquia mensal no Service Desk' },
    {
      iconeNome: 'calendar-clock',
      texto: 'Reuniões periódicas de alinhamento tático e estratégico',
    },
    {
      iconeNome: 'shield-check',
      texto: 'SafeGuardian completo — CloudOps, FinOps, Monitoramento e Governança',
    },
    {
      iconeNome: 'bar-chart-3',
      texto: 'Análise anual de arquitetura com recomendações por aplicação',
    },
    {
      iconeNome: 'hand-coins',
      texto: 'Gestão contínua de custos com suporte FinOps especializado',
    },
  ],
};

/**
 * Renderiza o bloco do plano de suporte escolhido.
 *
 * Layout: features à esquerda em grid, painel SafeGuardian à direita (reduzido para caber).
 * Texto em branco sobre fundo escuro com opacidade alta para garantir legibilidade.
 *
 * @returns HTML da seção, ou string vazia quando o plano é `nenhum` — nesse caso a nota na seção de
 * investimento já cobre a explicação.
 */
export function blocoPlanoSuporte(plano: PlanoSuporte, _trilha: Trilha): string {
  if (plano === 'nenhum') return '';

  const beneficios = BENEFICIOS[plano];
  const nome = NOME_COMERCIAL_PLANO[plano];

  const lista = beneficios
    .map(
      (b) =>
        `<li class="flex items-start gap-3">
                        <span class="shrink-0 mt-0.5 text-brand-accent">${icone(b.iconeNome, 'w-5 h-5')}</span>
                        <span class="text-white/90 text-sm leading-relaxed">${escaparHtml(b.texto)}</span>
                    </li>`,
    )
    .join('\n                    ');

  return `
    <section id="suporte" class="scroll-mt-24 py-20 bg-brand-blue text-white overflow-hidden relative border-t border-white/10">
        <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none opacity-10">
            <div class="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-accent blur-3xl"></div>
            <div class="absolute bottom-0 -left-24 w-72 h-72 rounded-full bg-blue-500 blur-3xl"></div>
        </div>
        <div class="${CONTAINER} relative z-10">
            <div class="mb-10 max-w-3xl">
                <span class="text-sm font-semibold text-brand-accent uppercase tracking-wider">Seu Plano de Suporte</span>
                <h2 class="text-3xl font-bold text-white mt-2 mb-4">${escaparHtml(nome)}</h2>
                <div class="w-20 h-1.5 bg-brand-accent rounded-full"></div>
                <p class="text-white/80 text-lg leading-relaxed mt-6">
                    O suporte da eMaster vai além do ticket: é acompanhamento consultivo, monitoramento proativo e acesso direto ao time técnico. Seu ambiente é tratado como se fosse nosso.
                </p>
            </div>

            <div class="grid lg:grid-cols-5 gap-8 items-start">
                <!-- Features do plano (3 colunas) -->
                <div class="lg:col-span-3 bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
                    <ul class="grid sm:grid-cols-2 gap-4">
                        ${lista}
                    </ul>
                </div>

                <!-- SafeGuardian (2 colunas) -->
                <div class="lg:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
                    <div class="flex items-center gap-3 mb-4">
                        <img src="https://committed.proposta.emaster.info/images/safeguardian-logo.png" alt="SafeGuardian" class="h-9 w-auto opacity-90" onerror="this.style.display='none';">
                        <div>
                            <h4 class="font-bold text-white text-sm leading-tight">Painéis SafeGuardian inclusos</h4>
                            <p class="text-xs text-white/70">CloudOps · FinOps · Monitoramento · Governança</p>
                        </div>
                    </div>
                    <p class="text-xs text-white/80 leading-relaxed mb-4">
                        Dashboards com métricas em tempo real, alertas inteligentes e recomendações acionáveis — sua visibilidade completa do ambiente.
                    </p>
                    <img src="https://emaster.cloud/wp-content/uploads/sites/10/2025/04/Homepage-gif.b6975003e97258da31aa36ac798a2b3f214b7525.gif" alt="Painel SafeGuardian" class="rounded-lg border border-white/10 w-full object-cover" onerror="this.style.display='none';">
                    <p class="text-xs text-white/50 text-center mt-2">Imagem ilustrativa: Dashboard SafeGuardian</p>
                </div>
            </div>
        </div>
    </section>`;
}
